import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, convertImageToBase64, deleteImage } from '../utils/imageUpload';
import { showSuccess, showError, showInfo, showWarning } from '../utils/toast';
import QuestionLibrary from './QuestionLibrary';

const ManageQuestions = ({ examId, examName }) => {
  const [questions, setQuestions] = useState([]);
  const [exam, setExam] = useState(null);
  const [activeTab, setActiveTab] = useState('addNew'); // 'addNew' or 'fromLibrary'
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saveToLibrary, setSaveToLibrary] = useState(true); // Default to true
  const [bulkMode, setBulkMode] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, url: '' });
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionImage: '',
    questionType: 'mcq',
    marks: '',
    difficultyLevel: 'medium',
    sectionId: 0,
    subjectName: '',
    options: [
      { text: '', image: '', isCorrect: false },
      { text: '', image: '', isCorrect: false }
    ],
    correctAnswer: ''
  });

  useEffect(() => {
    const loadQuestions = async () => {
      if (examId) {
        await fetchExam();
        await fetchQuestions();
      }
    };
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  const fetchExam = async () => {
    try {
      const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
      const examDoc = await getDoc(firestoreDoc(db, 'exams', examId));
      if (examDoc.exists()) {
        setExam({ id: examDoc.id, ...examDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching exam:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(
        collection(db, 'questions'), 
        where('examId', '==', examId),
        where('organizationId', '==', adminId)
      );
      const querySnapshot = await getDocs(q);
      const questionsList = [];
      querySnapshot.forEach((doc) => {
        questionsList.push({ id: doc.id, ...doc.data() });
      });
      setQuestions(questionsList);
    } catch (error) {
      console.error('Error fetching questions:', error);
      showError('Failed to load questions');
    }
  };

  const resetForm = () => {
    setQuestionForm({
      questionText: '',
      questionImage: '',
      questionType: 'mcq',
      marks: '',
      difficultyLevel: 'medium',
      sectionId: 0,
      subjectName: '',
      options: [
        { text: '', image: '', isCorrect: false },
        { text: '', image: '', isCorrect: false }
      ],
      correctAnswer: ''
    });
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!questionForm.questionText && !questionForm.questionImage) {
        showError('Please provide either question text or image');
        setLoading(false);
        return;
      }

      if (!questionForm.marks || questionForm.marks <= 0) {
        showError('Please enter valid marks');
        setLoading(false);
        return;
      }

      // Check if section is full (only if exam has sections)
      if (exam?.sections && exam.sections.length > 0) {
        const sectionId = parseInt(questionForm.sectionId) || 0;
        const sectionQuestions = questions.filter(q => q.sectionId === sectionId);
        const section = exam.sections[sectionId];
        const expectedCount = parseInt(section?.questions) || 0;
        const actualCount = sectionQuestions.length;
        
        if (actualCount >= expectedCount) {
          showError(`Section "${section?.name || `Section ${sectionId + 1}`}" is full (${actualCount}/${expectedCount}). Please select another section.`);
          setLoading(false);
          return;
        }
      }

      // Check for duplicate question
      const isDuplicate = questions.some(q => 
        q.questionText.trim().toLowerCase() === questionForm.questionText.trim().toLowerCase()
      );
      
      if (isDuplicate) {
        showError('This question already exists in the exam. Please add a different question.');
        setLoading(false);
        return;
      }

      if (questionForm.questionType === 'mcq') {
        const validOptions = questionForm.options.filter(opt => opt.text || opt.image);
        if (validOptions.length < 2) {
          showError('MCQ must have at least 2 options');
          setLoading(false);
          return;
        }

        const correctOptions = questionForm.options.filter(opt => opt.isCorrect);
        if (correctOptions.length === 0) {
          showError('Please mark at least one correct answer');
          setLoading(false);
          return;
        }
      }

      const questionData = {
        questionText: questionForm.questionText,
        questionImage: questionForm.questionImage,
        questionType: questionForm.questionType,
        marks: parseFloat(questionForm.marks),
        difficultyLevel: questionForm.difficultyLevel,
        sectionId: parseInt(questionForm.sectionId) || 0,
        subjectName: questionForm.subjectName || '',
        examId: examId,
        examName: examName,
        organizationId: localStorage.getItem('adminId'),
        createdAt: new Date().toISOString()
      };

      if (questionForm.questionType === 'mcq') {
        questionData.options = questionForm.options.filter(opt => opt.text || opt.image);
      } else {
        questionData.correctAnswer = questionForm.correctAnswer;
      }

      // Add to exam
      await addDoc(collection(db, 'questions'), questionData);

      // Save to library if checkbox is checked
      if (saveToLibrary) {
        const libraryData = { ...questionData };
        delete libraryData.examId;
        delete libraryData.examName;
        await addDoc(collection(db, 'questionLibrary'), libraryData);
        showSuccess('Question added to exam and saved to library!');
      } else {
        showSuccess('Question added to exam!');
      }

      if (bulkMode) {
        resetForm();
        showInfo('Ready to add next question');
      } else {
        resetForm();
        setShowQuestionForm(false);
      }
      
      await fetchQuestions();
    } catch (error) {
      console.error('Error adding question:', error);
      showError('Failed to add question: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMultipleFromLibrary = async (selectedQuestions, targetSectionId = 0) => {
    if (selectedQuestions.length === 0) {
      showWarning('Please select at least one question');
      return;
    }

    // Check for duplicates
    const duplicates = [];
    selectedQuestions.forEach(libQuestion => {
      const isDuplicate = questions.some(q => 
        q.questionText.trim().toLowerCase() === libQuestion.questionText.trim().toLowerCase()
      );
      if (isDuplicate) {
        duplicates.push(libQuestion.questionText.substring(0, 50) + '...');
      }
    });

    if (duplicates.length > 0) {
      showError(`${duplicates.length} question(s) already exist in this exam. Duplicates will be skipped.`);
    }

    // Filter out duplicates
    const uniqueQuestions = selectedQuestions.filter(libQuestion => 
      !questions.some(q => 
        q.questionText.trim().toLowerCase() === libQuestion.questionText.trim().toLowerCase()
      )
    );

    if (uniqueQuestions.length === 0) {
      showWarning('All selected questions already exist in this exam.');
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      uniqueQuestions.forEach((question) => {
        const questionRef = doc(collection(db, 'questions'));
        const questionData = {
          ...question,
          examId: examId,
          examName: examName,
          sectionId: parseInt(targetSectionId) || 0,
          organizationId: localStorage.getItem('adminId'),
          addedAt: new Date().toISOString()
        };
        delete questionData.id; // Remove library ID
        batch.set(questionRef, questionData);
      });

      await batch.commit();
      showSuccess(`${selectedQuestions.length} question(s) added to exam!`);
      await fetchQuestions();
    } catch (error) {
      console.error('Error adding questions from library:', error);
      showError('Failed to add questions from library');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      const questionToDelete = questions.find(q => q.id === questionId);
      
      // Delete question image if exists
      if (questionToDelete?.questionImage) {
        await deleteImage(questionToDelete.questionImage);
      }
      
      // Delete option images if exists
      if (questionToDelete?.options && questionToDelete.options.length > 0) {
        for (const option of questionToDelete.options) {
          if (option.image) {
            await deleteImage(option.image);
          }
        }
      }
      
      await deleteDoc(doc(db, 'questions', questionId));
      showSuccess('Question deleted successfully!');
      await fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      showError('Failed to delete question');
    }
  };

  const handleActivateExam = async () => {
    try {
      // Get exam details
      const { doc: firestoreDoc, getDoc, updateDoc } = await import('firebase/firestore');
      const examDoc = await getDoc(firestoreDoc(db, 'exams', examId));
      
      if (!examDoc.exists()) {
        showError('Exam not found');
        return;
      }

      const exam = examDoc.data();
      
      // Skip question validation for random selection mode
      if (exam.useRandomSelection) {
        // For random selection, just check if subjects are selected
        if (!exam.selectedSubjects || exam.selectedSubjects.length === 0) {
          showError('Cannot activate exam without selected subjects.');
          return;
        }
        
        // Proceed to activate without question count validation
        const examRef = firestoreDoc(db, 'exams', examId);
        await updateDoc(examRef, {
          status: 'active',
          updatedAt: new Date().toISOString()
        });
        
        showSuccess('Exam activated successfully! Students can now take this exam.');
        return;
      }
      
      // Normal validation for non-random exams
      const expectedQuestions = parseInt(exam.totalQuestions);
      const expectedTotalMarks = parseFloat(exam.totalMarks);
      const actualQuestions = questions.length;

      // Calculate actual total marks
      let actualTotalMarks = 0;
      questions.forEach(q => {
        actualTotalMarks += parseFloat(q.marks || 0);
      });

      // Validation checks
      if (actualQuestions === 0) {
        showError('Cannot activate exam without questions. Please add questions first.');
        return;
      }

      if (actualQuestions !== expectedQuestions) {
        showError(
          `Question count mismatch! Expected: ${expectedQuestions}, Added: ${actualQuestions}. Please ${
            actualQuestions < expectedQuestions 
              ? 'add ' + (expectedQuestions - actualQuestions) + ' more question(s)' 
              : 'remove ' + (actualQuestions - expectedQuestions) + ' question(s)'
          }.`
        );
        return;
      }

      if (Math.abs(actualTotalMarks - expectedTotalMarks) > 0.01) {
        showError(
          `Total marks mismatch! Expected: ${expectedTotalMarks}, Actual: ${actualTotalMarks.toFixed(2)}. Please adjust question marks to match the total.`
        );
        return;
      }

      // All validations passed, activate the exam
      const confirmActivate = window.confirm(
        `Ready to activate exam!\n\n✓ Questions: ${actualQuestions}/${expectedQuestions}\n✓ Total Marks: ${actualTotalMarks}/${expectedTotalMarks}\n\nDo you want to activate this exam now?`
      );

      if (!confirmActivate) return;

      await updateDoc(firestoreDoc(db, 'exams', examId), {
        status: 'active',
        updatedAt: new Date().toISOString()
      });

      showSuccess('Exam activated successfully! Students can now take this exam.');
      
    } catch (error) {
      console.error('Error activating exam:', error);
      showError('Failed to activate exam: ' + error.message);
    }
  };

  const handleEditQuestion = (question) => {
    showInfo('Edit functionality coming soon!');
    // TODO: Implement edit functionality
    // setEditingQuestion(question);
    // setQuestionForm({
    //   questionText: question.questionText,
    //   questionImage: question.questionImage,
    //   questionType: question.questionType,
    //   marks: question.marks,
    //   difficultyLevel: question.difficultyLevel,
    //   options: question.options || [],
    //   correctAnswer: question.correctAnswer || ''
    // });
    // setShowQuestionForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setQuestionForm({
      ...questionForm,
      [name]: value
    });
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index][field] = value;
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const handleQuestionImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }

    setUploadingImage(true);
    const result = await uploadImage(file, 'oesweb', 'questions');
    
    if (result.success) {
      setQuestionForm({ ...questionForm, questionImage: result.url });
      setUploadingImage(false);
      showSuccess('Image uploaded successfully!');
    } else {
      try {
        const base64Image = await convertImageToBase64(file);
        setQuestionForm({ ...questionForm, questionImage: base64Image });
        setUploadingImage(false);
        showSuccess('Image loaded successfully!');
      } catch (base64Error) {
        setUploadingImage(false);
        showError('Image upload failed: ' + result.error);
      }
    }
  };

  const handleOptionImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }

    setUploadingImage(true);
    const result = await uploadImage(file, 'oesweb', 'options');
    
    if (result.success) {
      const newOptions = [...questionForm.options];
      newOptions[index].image = result.url;
      setQuestionForm({ ...questionForm, options: newOptions });
      setUploadingImage(false);
      showSuccess('Option image uploaded successfully!');
    } else {
      try {
        const base64Image = await convertImageToBase64(file);
        const newOptions = [...questionForm.options];
        newOptions[index].image = base64Image;
        setQuestionForm({ ...questionForm, options: newOptions });
        setUploadingImage(false);
        showSuccess('Image loaded successfully!');
      } catch (base64Error) {
        setUploadingImage(false);
        showError('Image upload failed: ' + result.error);
      }
    }
  };

  const addOption = () => {
    setQuestionForm({
      ...questionForm,
      options: [...questionForm.options, { text: '', image: '', isCorrect: false }]
    });
  };

  const removeOption = (index) => {
    const newOptions = questionForm.options.filter((_, i) => i !== index);
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
        <h2 className="text-3xl font-bold text-white mb-2">Manage Questions</h2>
        <p className="text-indigo-100">Exam: {examName}</p>
        <div className="mt-3 flex flex-wrap gap-4">
          <p className="text-indigo-100 text-sm font-semibold">Total Questions: {questions.length}</p>
          {exam?.sections && exam.sections.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {exam.sections.map((section, index) => {
                const sectionQuestions = questions.filter(q => q.sectionId === index);
                const expectedCount = parseInt(section.questions) || 0;
                const actualCount = sectionQuestions.length;
                const isMatch = actualCount === expectedCount;
                return (
                  <div key={index} className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isMatch ? 'bg-green-500 text-white' : 
                    actualCount > expectedCount ? 'bg-red-500 text-white' :
                    'bg-yellow-500 text-white'
                  }`}>
                    {section.name || `Section ${index + 1}`}: {actualCount}/{expectedCount}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation - Hide for random selection exams */}
      {!exam?.useRandomSelection && (
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('addNew')}
              className={`px-8 py-4 font-semibold transition ${
                activeTab === 'addNew'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                  : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-100'
              }`}
            >
              Add New Questions
            </button>
            <button
              onClick={() => setActiveTab('fromLibrary')}
              className={`px-8 py-4 font-semibold transition ${
                activeTab === 'fromLibrary'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                  : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-100'
              }`}
            >
              Add from Library
            </button>
          </div>
        </div>
      )}

      <div className="p-8">
        {exam?.useRandomSelection ? (
          // Random Selection Mode - No manual question adding needed
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl border-2 border-blue-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                  <svg className="h-9 w-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">🎲 Random Selection Mode Active</h3>
                  <p className="text-gray-700 text-lg mb-4">
                    This exam uses <span className="font-semibold text-indigo-600">automatic random question selection</span>. You don't need to manually add questions here.
                  </p>
                  <div className="bg-white p-4 rounded-lg border border-indigo-200 space-y-2">
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">How it works:</span></p>
                    <ul className="space-y-2 text-gray-700 ml-4">
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>Questions are automatically selected from the <span className="font-semibold">Question Library</span> when students start the exam</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>Each student receives a <span className="font-semibold">unique random set</span> of questions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>Questions are distributed equally across the selected subjects</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>Make sure you have enough questions in the Question Library for each subject</span>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      <span className="font-semibold">💡 Tip:</span> To ensure variety, add more questions to your Question Library for the selected subjects: <span className="font-semibold">{exam.selectedSubjects?.join(', ')}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'addNew' ? (
          <div className="space-y-6">
            {/* Add Question Button & Bulk Mode */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowQuestionForm(!showQuestionForm)}
                className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2"
              >
                {showQuestionForm ? (
                  <>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Close Form
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add New Question
                  </>
                )}
              </button>

              {showQuestionForm && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkMode}
                    onChange={(e) => setBulkMode(e.target.checked)}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="font-semibold text-gray-700">Bulk Add Mode (Keep form open)</span>
                </label>
              )}
            </div>

            {/* Question Form */}
            {showQuestionForm && (
              <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                <form onSubmit={handleAddQuestion} className="space-y-6">
                  {/* Question Text */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Question Text</label>
                    <textarea
                      value={questionForm.questionText}
                      onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      placeholder="Enter your question here..."
                    />
                  </div>

                  {/* Question Image Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Question Image (Optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleQuestionImageUpload}
                      disabled={uploadingImage}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    />
                    {uploadingImage && <p className="text-sm text-indigo-600 mt-2">Uploading image...</p>}
                    {questionForm.questionImage && (
                      <div className="mt-3">
                        <img src={questionForm.questionImage} alt="Question" className="max-w-xs rounded-lg border border-gray-200" />
                        <button
                          type="button"
                          onClick={async () => {
                            await deleteImage(questionForm.questionImage);
                            setQuestionForm({ ...questionForm, questionImage: '' });
                            showInfo('Image removed');
                          }}
                          className="text-sm text-red-600 hover:text-red-800 mt-2"
                        >
                          Remove Image
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Subject/Concept Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Subject / Concept Name (Optional)</label>
                    <input
                      type="text"
                      name="subjectName"
                      value={questionForm.subjectName}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      placeholder="e.g., Physics - Mechanics, Math - Algebra, etc."
                    />
                    <p className="text-xs text-gray-500 mt-1">Helps categorize questions by subject or topic</p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    {/* Section Selector */}
                    {exam?.sections && exam.sections.length > 1 && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Section *
                        </label>
                        <select
                          value={questionForm.sectionId}
                          onChange={(e) => setQuestionForm({ ...questionForm, sectionId: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                          required
                        >
                          {exam.sections.map((section, index) => {
                            const sectionQuestions = questions.filter(q => q.sectionId === index);
                            const expectedCount = parseInt(section.questions) || 0;
                            const actualCount = sectionQuestions.length;
                            const isFull = actualCount >= expectedCount;
                            return (
                              <option key={index} value={index}>
                                {section.name || `Section ${index + 1}`} ({actualCount}/{expectedCount}) {isFull ? '✓ Full' : `- ${expectedCount - actualCount} needed`}
                              </option>
                            );
                          })}
                        </select>
                        {(() => {
                          const sectionQuestions = questions.filter(q => q.sectionId === parseInt(questionForm.sectionId));
                          const section = exam.sections[parseInt(questionForm.sectionId)];
                          const expectedCount = parseInt(section?.questions) || 0;
                          const actualCount = sectionQuestions.length;
                          const isFull = actualCount >= expectedCount;
                          
                          if (isFull) {
                            return (
                              <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  <span className="text-sm font-semibold text-red-800">
                                    This section is full! Please select another section.
                                  </span>
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    )}

                    {/* Question Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Question Type *</label>
                      <select
                        name="questionType"
                        value={questionForm.questionType}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      >
                        <option value="mcq">Multiple Choice (MCQ)</option>
                        <option value="short">Short Answer</option>
                        <option value="long">Long Answer</option>
                      </select>
                    </div>

                    {/* Marks */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Marks *</label>
                      <input
                        type="number"
                        name="marks"
                        required
                        min="0.5"
                        step="0.5"
                        value={questionForm.marks}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="e.g., 2"
                      />
                    </div>

                    {/* Difficulty Level */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty Level *</label>
                      <select
                        name="difficultyLevel"
                        value={questionForm.difficultyLevel}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                  </div>

                  {/* MCQ Options */}
                  {questionForm.questionType === 'mcq' && (
                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex justify-between items-center mb-4">
                        <h5 className="text-lg font-bold text-gray-900">Options</h5>
                        <button
                          type="button"
                          onClick={addOption}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition flex items-center gap-2"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Option
                        </button>
                      </div>

                      <div className="space-y-4">
                        {questionForm.options.map((option, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-center mb-3">
                              <span className="font-semibold text-gray-700">Option {index + 1}</span>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={option.isCorrect}
                                    onChange={(e) => handleOptionChange(index, 'isCorrect', e.target.checked)}
                                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                  />
                                  <span className="text-sm font-medium text-green-700">Correct Answer</span>
                                </label>
                                {questionForm.options.length > 2 && (
                                  <button
                                    type="button"
                                    onClick={() => removeOption(index)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                  >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="space-y-3">
                              <input
                                type="text"
                                value={option.text}
                                onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                                placeholder="Option text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                              />

                              <div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleOptionImageUpload(index, e)}
                                  disabled={uploadingImage}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition text-sm"
                                />
                                {option.image && (
                                  <div className="mt-2">
                                    <img src={option.image} alt={`Option ${index + 1}`} className="max-w-xs rounded-lg border border-gray-200" />
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        await deleteImage(option.image);
                                        handleOptionChange(index, 'image', '');
                                        showInfo('Option image removed');
                                      }}
                                      className="text-sm text-red-600 hover:text-red-800 mt-1"
                                    >
                                      Remove Image
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Correct Answer for Short/Long Answer */}
                  {(questionForm.questionType === 'short' || questionForm.questionType === 'long') && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Expected Answer / Keywords (Optional)
                      </label>
                      <textarea
                        name="correctAnswer"
                        value={questionForm.correctAnswer}
                        onChange={handleFormChange}
                        rows="3"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                        placeholder="Enter expected answer or keywords for reference..."
                      />
                    </div>
                  )}

                  {/* Save to Library Checkbox */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={saveToLibrary}
                        onChange={(e) => setSaveToLibrary(e.target.checked)}
                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <div>
                        <span className="font-semibold text-gray-900">Save to Question Library</span>
                        <p className="text-sm text-gray-600">Save this question to library for reuse in future exams</p>
                      </div>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={loading || uploadingImage}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
                    >
                      {loading ? 'Adding...' : bulkMode ? 'Add & Continue' : 'Add Question'}
                    </button>
                    {!bulkMode && (
                      <button
                        type="button"
                        onClick={() => {
                          resetForm();
                          setShowQuestionForm(false);
                        }}
                        className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* Validation Status Panel */}
            {exam && (
              (() => {
                // Check if this is a random selection exam
                if (exam.useRandomSelection) {
                  const hasSelectedSubjects = exam.selectedSubjects && exam.selectedSubjects.length > 0;
                  const isReady = hasSelectedSubjects;

                  return (
                    <div className={`mb-6 p-6 rounded-xl border-2 ${
                      isReady 
                        ? 'bg-green-50 border-green-500' 
                        : 'bg-yellow-50 border-yellow-500'
                    }`}>
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                          isReady ? 'bg-green-500' : 'bg-yellow-500'
                        }`}>
                          {isReady ? (
                            <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-bold text-lg mb-3 ${isReady ? 'text-green-900' : 'text-yellow-900'}`}>
                            {isReady ? '✓ Exam Ready to Activate!' : '⚠ Configuration Required'}
                          </h4>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-blue-500">
                                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">🎲 Random Selection Mode Active</p>
                                <p className="text-sm text-gray-600 mt-1">
                                  Questions will be automatically selected from the question library when students take the exam.
                                </p>
                              </div>
                            </div>
                            
                            {hasSelectedSubjects && (
                              <div className="bg-white p-4 rounded-lg border border-green-200">
                                <p className="font-semibold text-green-900 mb-2">📚 Question Distribution</p>
                                <div className="space-y-1 text-sm">
                                  <p className="text-gray-700"><span className="font-semibold">Total Questions:</span> {exam.totalQuestions}</p>
                                  <p className="text-gray-700"><span className="font-semibold">Selected Subjects:</span> {exam.selectedSubjects.join(', ')}</p>
                                  <p className="text-gray-700">
                                    <span className="font-semibold">Distribution:</span> ~{Math.floor(exam.totalQuestions / exam.selectedSubjects.length)} questions per subject
                                  </p>
                                </div>
                              </div>
                            )}

                            {!hasSelectedSubjects && (
                              <p className="text-sm text-yellow-800 font-medium bg-yellow-100 p-3 rounded">
                                ⚠ No subjects selected. Please edit the exam to add subjects.
                              </p>
                            )}

                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                              <p className="text-sm text-blue-800">
                                <span className="font-semibold">ℹ Note:</span> You don't need to manually add questions. Each student will receive random questions from the selected subjects during the exam.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Normal mode validation
                const expectedQuestions = parseInt(exam.totalQuestions || 0);
                const expectedMarks = parseFloat(exam.totalMarks || 0);
                const actualQuestions = questions.length;
                const actualMarks = questions.reduce((sum, q) => sum + parseFloat(q.marks || 0), 0);
                const questionsMatch = actualQuestions === expectedQuestions;
                const marksMatch = Math.abs(actualMarks - expectedMarks) < 0.01;
                const isReady = questionsMatch && marksMatch && actualQuestions > 0;

                return (
                  <div className={`mb-6 p-6 rounded-xl border-2 ${
                    isReady 
                      ? 'bg-green-50 border-green-500' 
                      : 'bg-yellow-50 border-yellow-500'
                  }`}>
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                        isReady ? 'bg-green-500' : 'bg-yellow-500'
                      }`}>
                        {isReady ? (
                          <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-lg mb-3 ${isReady ? 'text-green-900' : 'text-yellow-900'}`}>
                          {isReady ? '✓ Exam Ready to Activate!' : '⚠ Validation Required'}
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              questionsMatch ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                              {questionsMatch ? (
                                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                            </div>
                            <span className="font-semibold text-gray-900">
                              Questions: {actualQuestions}/{expectedQuestions}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              marksMatch ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                              {marksMatch ? (
                                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                            </div>
                            <span className="font-semibold text-gray-900">
                              Total Marks: {actualMarks.toFixed(2)}/{expectedMarks}
                            </span>
                          </div>
                        </div>
                        {!isReady && (
                          <p className="mt-3 text-sm text-yellow-800 font-medium">
                            {!questionsMatch && `Need ${actualQuestions < expectedQuestions ? (expectedQuestions - actualQuestions) + ' more' : (actualQuestions - expectedQuestions) + ' less'} question(s). `}
                            {!marksMatch && `Marks need to total ${expectedMarks} (currently ${actualMarks.toFixed(2)}).`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()
            )}

            {/* Questions List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {exam?.useRandomSelection ? 'Questions (Auto-Selected from Library)' : `Questions Bank (${questions.length})`}
                </h3>
                {/* Show Activate button for random selection exams OR when questions exist for normal exams */}
                {(exam?.useRandomSelection || questions.length > 0) && (
                  <button
                    onClick={handleActivateExam}
                    className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Activate Exam Now
                  </button>
                )}
              </div>
              {questions.length === 0 && !exam?.useRandomSelection ? (
                <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                  <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-md">
                    <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-semibold text-lg">No questions added yet</p>
                  <p className="text-gray-500 text-sm mt-2">Start building your question bank for this exam</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {questions.map((question, index) => (
                    <div key={question.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
                      {/* Question Header */}
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="bg-indigo-600 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg shadow-md">
                              {index + 1}
                            </div>
                            {exam?.sections && exam.sections.length > 1 && (
                              <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-500 text-white shadow-sm">
                                {exam.sections[question.sectionId || 0]?.name || `Section ${(question.sectionId || 0) + 1}`}
                              </span>
                            )}
                            <span className={`px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
                              question.questionType === 'mcq' ? 'bg-blue-500 text-white' :
                              question.questionType === 'short' ? 'bg-green-500 text-white' :
                              'bg-purple-500 text-white'
                            }`}>
                              {question.questionType === 'mcq' ? '📝 MCQ' : 
                               question.questionType === 'short' ? '✍️ Short Answer' : '📄 Long Answer'}
                            </span>
                            <span className={`px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
                              question.difficultyLevel === 'easy' ? 'bg-green-500 text-white' :
                              question.difficultyLevel === 'medium' ? 'bg-yellow-500 text-white' :
                              'bg-red-500 text-white'
                            }`}>
                              {question.difficultyLevel === 'easy' ? '🟢 Easy' :
                               question.difficultyLevel === 'medium' ? '🟡 Medium' : '🔴 Hard'}
                            </span>
                            <span className="px-4 py-1.5 bg-indigo-600 text-white rounded-full text-sm font-semibold shadow-sm">
                              🎯 {question.marks} {question.marks === 1 ? 'Mark' : 'Marks'}
                            </span>
                            {question.subjectName && (
                              <span className="px-4 py-1.5 bg-orange-500 text-white rounded-full text-sm font-semibold shadow-sm">
                                📚 {question.subjectName}
                              </span>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
                              className="p-2 hover:bg-indigo-100 rounded-lg transition-colors"
                              title={expandedQuestion === question.id ? 'Collapse' : 'Expand'}
                            >
                              <svg className={`h-5 w-5 text-indigo-600 transition-transform ${expandedQuestion === question.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleEditQuestion(question)}
                              className="p-2 hover:bg-blue-100 rounded-lg transition-colors group"
                              title="Edit Question"
                            >
                              <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteQuestion(question.id)}
                              className="p-2 hover:bg-red-100 rounded-lg transition-colors group"
                              title="Delete Question"
                            >
                              <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Question Content */}
                      <div className={`px-6 py-4 ${expandedQuestion === question.id ? 'block' : 'hidden'}`}>
                        {/* Question Text */}
                        {question.questionText && (
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Question</p>
                            <p className="text-gray-900 text-lg leading-relaxed">{question.questionText}</p>
                          </div>
                        )}
                        
                        {/* Question Image */}
                        {question.questionImage && (
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Question Image</p>
                            <div className="relative inline-block group cursor-pointer" onClick={() => setImageModal({ show: true, url: question.questionImage })}>
                              <img 
                                src={question.questionImage} 
                                alt="Question" 
                                className="max-w-xs h-40 object-cover rounded-lg border-2 border-gray-200 shadow-sm hover:shadow-md transition-all" 
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all flex items-center justify-center">
                                <svg className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* MCQ Options */}
                        {question.questionType === 'mcq' && question.options && (
                          <div className="mt-4">
                            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Answer Options</p>
                            <div className="grid gap-3">
                              {question.options.map((option, optIndex) => (
                                <div 
                                  key={optIndex} 
                                  className={`relative p-4 rounded-xl border-2 transition-all ${
                                    option.isCorrect 
                                      ? 'bg-green-50 border-green-400 shadow-sm' 
                                      : 'bg-white border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                                      option.isCorrect ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
                                    }`}>
                                      {String.fromCharCode(65 + optIndex)}
                                    </div>
                                    <div className="flex-1">
                                      {option.text && <p className="text-gray-800 font-medium">{option.text}</p>}
                                      {option.image && (
                                        <div className="mt-2 relative inline-block group cursor-pointer" onClick={() => setImageModal({ show: true, url: option.image })}>
                                          <img 
                                            src={option.image} 
                                            alt={`Option ${optIndex + 1}`} 
                                            className="max-w-xs h-32 object-cover rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all" 
                                          />
                                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all flex items-center justify-center">
                                            <svg className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                            </svg>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {option.isCorrect && (
                                      <div className="flex-shrink-0">
                                        <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          Correct
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Answer for Short/Long Questions */}
                        {(question.questionType === 'short' || question.questionType === 'long') && question.correctAnswer && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm font-semibold text-blue-800 uppercase tracking-wide mb-2">Expected Answer / Keywords</p>
                            <p className="text-gray-700">{question.correctAnswer}</p>
                          </div>
                        )}
                      </div>

                      {/* Collapsed Preview */}
                      {expandedQuestion !== question.id && (
                        <div className="px-6 py-3 bg-gray-50">
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {question.questionText || (question.questionImage ? '🖼️ Image-based question' : 'No question text')}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Image Modal */}
            {imageModal.show && (
              <div 
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                onClick={() => setImageModal({ show: false, url: '' })}
              >
                <div className="relative max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setImageModal({ show: false, url: '' })}
                    className="absolute -top-4 -right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
                  >
                    <svg className="h-6 w-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <img 
                    src={imageModal.url} 
                    alt="Zoomed" 
                    className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <QuestionLibrary 
            onAddToExam={handleAddMultipleFromLibrary} 
            exam={exam}
            currentQuestions={questions}
          />
        )}
      </div>
    </div>
  );
};

export default ManageQuestions;
